import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, NativeSelect, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EMPTY = { title: "", body: "", category: "General", priority: "normal", image_url: "" };
const AUDIENCE = [["all", "All consumers"], ["municipality", "Municipalities"], ["barangay", "Barangays"], ["account_numbers", "Account numbers"]];
const TYPES = ["announcement", "outage", "billing", "payment", "support", "account"];

function AnnouncementEditor({ value, onDone }) {
  const [f, setF] = useState(value);
  useEffect(() => { setF(value); }, [value]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { title: f.title, body: f.body, category: f.category, priority: f.priority, image_url: f.image_url || null };
    try { f.id ? await api.put(`/admin/announcements/${f.id}`, body) : await api.post("/admin/announcements", body); toast.success(f.id ? "Announcement saved" : "Saved as draft"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={f.id ? "Edit announcement" : "New announcement"} testId="announcement-editor">
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={set("title")} data-testid="ann-title" /></div>
        <div className="space-y-1.5"><Label>Message</Label><Textarea rows={4} value={f.body} onChange={set("body")} data-testid="ann-body" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Category</Label><Input value={f.category} onChange={set("category")} data-testid="ann-category" /></div>
          <div className="space-y-1.5"><Label>Priority</Label><NativeSelect value={f.priority} onChange={(v) => setF({ ...f, priority: v })} testId="ann-priority"
            options={["low", "normal", "high"].map((p) => ({ value: p, label: p }))} /></div>
        </div>
        <div className="space-y-1.5"><Label>Image URL (optional, https)</Label><Input value={f.image_url || ""} onChange={set("image_url")} data-testid="ann-image" /></div>
        <div className="flex gap-2"><Button onClick={save} data-testid="ann-save">Save</Button>{f.id && <Button variant="outline" onClick={() => onDone()}>Cancel</Button>}</div>
      </div>
    </Panel>
  );
}

function Broadcast() {
  const [f, setF] = useState({ title: "", body: "", type: "announcement", audience: "all", values: "" });
  const send = async () => {
    try {
      const r = await api.post("/admin/notifications/broadcast", { title: f.title, body: f.body, type: f.type,
        audience: { type: f.audience, values: f.values.split(",").map((x) => x.trim()).filter(Boolean) } });
      toast.success(`Sent to ${r.data.sent} of ${r.data.recipients} consumers (others turned this type off)`); setF({ ...f, title: "", body: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Send an in-app notification" description="Goes straight to consumers' mobile inbox. Respects their notification preferences." testId="broadcast-panel">
      <div className="space-y-3">
        <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="bc-title" />
        <Textarea rows={3} placeholder="Message" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="bc-body" />
        <div className="grid sm:grid-cols-2 gap-3">
          <NativeSelect value={f.type} onChange={(v) => setF({ ...f, type: v })} options={TYPES.map((t) => ({ value: t, label: t }))} testId="bc-type" />
          <NativeSelect value={f.audience} onChange={(v) => setF({ ...f, audience: v })} options={AUDIENCE.map(([v, l]) => ({ value: v, label: l }))} testId="bc-audience" />
        </div>
        {f.audience !== "all" && <Input placeholder="Comma-separated values" value={f.values} onChange={(e) => setF({ ...f, values: e.target.value })} data-testid="bc-values" />}
        <Button onClick={send} disabled={f.title.trim().length < 3 || f.body.trim().length < 3} data-testid="bc-send">Send notification</Button>
      </div>
    </Panel>
  );
}

export default function Announcements() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(EMPTY);
  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const toggle = async (a) => {
    try { const r = await api.post(`/admin/announcements/${a.id}/${a.published ? "unpublish" : "publish"}`); toast.success(a.published ? "Unpublished" : `Published, ${r.data.notified} notified`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Communications" title="Announcements" description="Published announcements appear in the mobile app's news feed." />
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 min-w-0">
        <div className="space-y-3 min-w-0">
          {items === null ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? <Empty>No announcements yet.</Empty> : items.map((a) => (
            <Panel key={a.id} testId={`announcement-${a.id}`}>
              <div className="flex flex-col sm:flex-row justify-between gap-3">
                <div className="min-w-0"><p className="font-semibold break-words">{a.title}</p>
                  <p className="text-xs text-slate-500">{a.category} · {a.priority} · {a.published_at ? `published ${fmtDate(a.published_at)}` : "draft"}</p>
                  <p className="text-sm mt-2 whitespace-pre-wrap break-words line-clamp-3">{a.body}</p></div>
                <div className="flex gap-2 items-start shrink-0"><StatusBadge tone={a.published ? "success" : "neutral"}>{a.published ? "Published" : "Draft"}</StatusBadge>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...a })} data-testid={`ann-edit-${a.id}`}>Edit</Button>
                  <Button size="sm" variant={a.published ? "outline" : "default"} onClick={() => toggle(a)} data-testid={`ann-publish-${a.id}`}>{a.published ? "Unpublish" : "Publish"}</Button></div>
              </div>
            </Panel>))}
        </div>
        <div className="space-y-6 min-w-0">
          <AnnouncementEditor value={editing} onDone={() => { setEditing({ ...EMPTY }); load(); }} />
          {can("notifications.send") && <Broadcast />}
        </div>
      </div>
    </div>
  );
}
